import { useRef, useEffect } from 'react';

const isMobileDevice = () => window.innerWidth < 768;

const MorphicBackground = ({ theme }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const targetRef = useRef({ x: 0.5, y: 0.5 });
  const themeRef = useRef(theme);

  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize, { passive: true });

    const onMouseMove = (e) => {
      targetRef.current.x = e.clientX / window.innerWidth;
      targetRef.current.y = e.clientY / window.innerHeight;
    };
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    const onTouchMove = (e) => {
      if (e.touches.length > 0) {
        targetRef.current.x = e.touches[0].clientX / window.innerWidth;
        targetRef.current.y = e.touches[0].clientY / window.innerHeight;
      }
    };
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    const mobile = isMobileDevice();

    const allBlobs = [
      { ox: 0.12, oy: 0.18, vx: 0.00015, vy: 0.00012, size: 0.28, phase: 0,    speed: 0.25, color: [220, 38, 38] },
      { ox: 0.88, oy: 0.25, vx: -0.0001,  vy: 0.00015, size: 0.32, phase: 2.1,  speed: 0.20, color: [185, 28, 28] },
      { ox: 0.35, oy: 0.80, vx: 0.00012,  vy: -0.0001, size: 0.24, phase: 4.2,  speed: 0.30, color: [239, 68, 68] },
      { ox: 0.70, oy: 0.65, vx: -0.00012, vy: -0.0001, size: 0.22, phase: 1.1,  speed: 0.28, color: [153, 27, 27] },
      { ox: 0.50, oy: 0.15, vx: 0.00008,  vy: 0.00018, size: 0.19, phase: 3.3,  speed: 0.35, color: [220, 38, 38] },
      { ox: 0.20, oy: 0.60, vx: 0.00018,  vy: -0.00015,size: 0.21, phase: 5.1,  speed: 0.22, color: [200, 30, 30] },
    ];

    const blobs = mobile ? allBlobs.slice(0, 3) : allBlobs;

    let t = 0;
    let frameCount = 0;
    const SKIP = mobile ? 2 : 1;

    const draw = () => {
      animRef.current = requestAnimationFrame(draw);
      frameCount++;
      if (frameCount % SKIP !== 0) return;

      const w = canvas.width;
      const h = canvas.height;
      const isDark = themeRef.current === 'dark';

      const lerp = mobile ? 0.025 : 0.04;
      mouseRef.current.x += (targetRef.current.x - mouseRef.current.x) * lerp;
      mouseRef.current.y += (targetRef.current.y - mouseRef.current.y) * lerp;

      ctx.clearRect(0, 0, w, h);

      ctx.fillStyle = isDark ? '#000000' : '#fafafa';
      ctx.fillRect(0, 0, w, h);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const drift = 0.06;
      const mouseInfluence = mobile ? 0.04 : 0.07;

      blobs.forEach((blob, i) => {
        const bx = (
          blob.ox
          + Math.sin(t * blob.vx * 1000 + blob.phase) * drift
          + (mx - 0.5) * mouseInfluence
        ) * w;
        const by = (
          blob.oy
          + Math.cos(t * blob.vy * 1000 + blob.phase) * drift
          + (my - 0.5) * mouseInfluence
        ) * h;
        const minDim = Math.min(w, h);
        const radius = blob.size * minDim * (0.85 + 0.15 * Math.sin(t * blob.speed + blob.phase));

        const alpha = isDark
          ? [0.22, 0.18, 0.20, 0.15, 0.13, 0.17][i]
          : [0.13, 0.10, 0.12, 0.09, 0.08, 0.11][i];

        const [r, g, b] = blob.color;
        const grad = ctx.createRadialGradient(bx, by, 0, bx, by, radius);
        grad.addColorStop(0,   `rgba(${r},${g},${b},${alpha})`);
        grad.addColorStop(0.45,`rgba(${r},${g},${b},${alpha * 0.4})`);
        grad.addColorStop(1,   `rgba(${r},${g},${b},0)`);

        ctx.beginPath();
        ctx.arc(bx, by, radius, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      });

      if (!mobile) {
        const mouseRadius = Math.min(w, h) * 0.18;
        const mAlpha = isDark ? 0.14 : 0.08;
        const mouseGrad = ctx.createRadialGradient(mx * w, my * h, 0, mx * w, my * h, mouseRadius);
        mouseGrad.addColorStop(0, `rgba(220,38,38,${mAlpha})`);
        mouseGrad.addColorStop(0.6, `rgba(220,38,38,${mAlpha * 0.3})`);
        mouseGrad.addColorStop(1, `rgba(220,38,38,0)`);
        ctx.beginPath();
        ctx.arc(mx * w, my * h, mouseRadius, 0, Math.PI * 2);
        ctx.fillStyle = mouseGrad;
        ctx.fill();
      }

      t += 0.016;
    };

    draw();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        display: 'block',
        width: '100%',
        height: '100%',
      }}
    />
  );
};

export default MorphicBackground;
